import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  AlarmClock,
  AlertTriangle,
  CalendarClock,
  MessageSquareWarning,
  Users,
} from "lucide-react";
import { api } from "@/lib/api/client";
import type { components } from "@/lib/api/generated/schema";
import { Skeleton } from "@/ui/skeleton";
import { StaffAvatar } from "@/components/StaffAvatar";
import { StaffTitle } from "@/features/cosmetics/StaffTitle";
import { formatDateTime } from "@/lib/format";
import { Badge } from "@/ui/badge";
import { cn } from "@/lib/utils";
import { stageLabel } from "../pipeline";
import { DeadlineSettingSection } from "../components/DeadlineSettingSection";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/table";
import { serverErrorMessage } from "@/lib/api/error-message";

/**
 * Màn "Quản lý" của CSKH — bức tranh toàn đội trong một trang.
 *
 * Mọi con số ở đây do MÁY CHỦ đếm trên toàn bộ dữ liệu trong phạm vi người
 * xem. Bản trước tự đếm ở trình duyệt: kéo 120 đơn đầu rồi bắn tới 160 lượt
 * gọi lẻ để gom nhật ký. Vừa chậm vừa SAI — đo 01/09/2026 có 6.360 đơn và
 * 1.024 hồ sơ CSKH, nên "120 đơn đầu" chưa tới 2% dữ liệu và mọi ô số trên
 * màn đều thiếu, không có gì báo cho người dùng biết là đang thiếu.
 */

type Summary = components["schemas"]["DashboardCskhSummary"];
type Activity = components["schemas"]["DashboardCskhActivity"];
type ActivityEntry = components["schemas"]["DashboardCskhActivityEntry"];

function useCskhSummary() {
  return useQuery({
    queryKey: ["dashboard", "cskh-summary"],
    queryFn: async () => {
      const { data, error } = await api.GET("/dashboard/cskh-summary", {});
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được số liệu CSKH"));
      return data as Summary;
    },
    staleTime: 60_000,
  });
}

function useCskhActivity() {
  return useQuery({
    queryKey: ["dashboard", "cskh-activity", 30],
    queryFn: async () => {
      const { data, error } = await api.GET("/dashboard/cskh-activity", {
        params: { query: { limit: 30 } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được sổ hoạt động CSKH"));
      return data as Activity;
    },
    staleTime: 60_000,
  });
}

export function ManageTab() {
  const summaryQ = useCskhSummary();
  const activityQ = useCskhActivity();

  if (summaryQ.isLoading || activityQ.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (summaryQ.error || activityQ.error) {
    // Báo thẳng thay vì hiện bảng số 0: một màn quản lý hiện toàn số 0 trông
    // y hệt "đội không có việc gì", nguy hiểm hơn hẳn một dòng báo lỗi.
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
        Không tải được số liệu CSKH. Thử tải lại trang.
      </div>
    );
  }

  const s = summaryQ.data;
  const a = activityQ.data;
  if (!s || !a) return null;

  const complaints = (s.actionList ?? []).filter((i) => i.isComplaint).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <KpiCard
          icon={<Users className="size-4" />}
          label="Khách đang xử lý"
          value={s.totalActive}
        />
        <KpiCard
          icon={<CalendarClock className="size-4" />}
          label="Việc đã làm hôm nay"
          value={s.todayActions}
          accent="blue"
        />
        <KpiCard
          icon={<MessageSquareWarning className="size-4" />}
          label="Khiếu nại đang mở"
          value={complaints}
          accent="red"
        />
        <KpiCard
          icon={<AlarmClock className="size-4" />}
          label="Sắp tới hạn"
          value={s.dueSoon}
          accent="amber"
        />
        <KpiCard
          icon={<AlertTriangle className="size-4" />}
          label="Lâu chưa xử lý"
          value={(s.overdueList ?? []).length}
          accent="red"
        />
      </div>

      <StaffActivityTable rows={a.byStaff ?? []} />
      <RecentActivityTable rows={a.recent ?? []} />
      <DeadlineSettingSection />
    </div>
  );
}

// ════════════════ Ai làm bao nhiêu ════════════════

function StaffActivityTable({
  rows,
}: {
  rows: Activity["byStaff"];
}) {
  return (
    <section className="space-y-2 rounded-md border bg-surface p-3">
      <h3 className="text-sm font-semibold">Hoạt động từng nhân viên</h3>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Chưa có nhân viên nào ghi việc vào sổ CSKH.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nhân viên</TableHead>
              <TableHead className="text-right">Khách đã chạm</TableHead>
              <TableHead className="text-right">Số việc</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.name}>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5">
                    <StaffAvatar name={c.name} sizePx={18} />
                    {c.name}
                    <StaffTitle name={c.name} />
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">{c.customers}</TableCell>
                <TableCell className="text-right tabular-nums">{c.actions}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

// ════════════════ Việc gần đây ════════════════

/** Khoá việc của ERP cũ ↦ câu tiếng Việt. Chỉ có đúng hai khoá trong dữ liệu
 *  thật (đo 01/09/2026: `auto_create` 991 dòng, `status_change` 288 dòng),
 *  nhưng vẫn để bảng tra để khoá lạ không rơi thẳng ra màn hình. */
const ACTION_LABEL: Record<string, string> = {
  auto_create: "Máy tự mở hồ sơ",
  status_change: "Đổi bước",
};

/** Câu mô tả một dòng nhật ký, viết như người nói.
 *
 *  Mũi tên CHỈ hiện khi thật sự có đổi bước. 251/288 dòng `status_change`
 *  trong dữ liệu thật có `from` bằng đúng `to` — vẽ "Retouch → Retouch" là
 *  bịa ra một chuyển động chưa từng xảy ra. */
function describe(e: ActivityEntry): string {
  if (e.kind === "complaint") {
    const cat = e.action ? `: ${e.action}` : "";
    return `Mở khiếu nại${cat}`;
  }
  const note = (e.note ?? "").trim();
  const from = e.from ?? "";
  const to = e.to ?? "";
  if (from && to && from !== to) {
    const moved = `${stageLabel(from)} → ${stageLabel(to)}`;
    return note ? `${moved} · ${note}` : moved;
  }
  const label = ACTION_LABEL[e.action ?? ""] ?? e.action ?? "";
  // Không đổi bước: nói việc đang ở bước nào, đừng vẽ mũi tên rỗng.
  const at = to || from;
  const where = at ? ` (${stageLabel(at)})` : "";
  const head = label ? `${label}${where}` : where.trim() || "Ghi sổ";
  return note ? `${head} · ${note}` : head;
}

function RecentActivityTable({ rows }: { rows: Activity["recent"] }) {
  return (
    <section className="space-y-2 rounded-md border bg-surface p-3">
      <h3 className="text-sm font-semibold">Việc gần đây</h3>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">Chưa có việc nào được ghi.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Thời gian</TableHead>
              <TableHead>Khách</TableHead>
              <TableHead>Người làm</TableHead>
              <TableHead>Việc</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((e, i) => (
              <TableRow key={`${e.orderId}-${e.at}-${i}`}>
                <TableCell className="whitespace-nowrap text-xs tabular-nums">
                  {formatDateTime(e.at)}
                </TableCell>
                <TableCell className="text-xs">
                  <Link
                    to="/orders/$orderId"
                    params={{ orderId: e.orderId }}
                    className="hover:underline"
                  >
                    <span className="font-medium">{e.customer || "Khách"}</span>
                    {e.orderCode ? (
                      <span className="ml-1 font-mono text-[10px] text-muted-foreground">
                        {e.orderCode}
                      </span>
                    ) : null}
                  </Link>
                </TableCell>
                <TableCell className="text-xs">
                  {/* Nhật ký ERP cũ ghi `system` cho việc máy tự làm, và bảng
                      khiếu nại không lưu người mở — cả hai đều KHÔNG phải công
                      của ai, nên không gắn ảnh đại diện. */}
                  {e.by && e.by !== "system" && e.by !== "system_sync" ? (
                    <span className="inline-flex items-center gap-1.5">
                      <StaffAvatar name={e.by} sizePx={18} />
                      {e.by}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      {e.by ? "Máy" : "—"}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-xs">
                  <span className="inline-flex items-center gap-1.5">
                    {e.kind === "complaint" ? (
                      <Badge variant="destructive" className="text-[10px]">
                        Khiếu nại
                      </Badge>
                    ) : null}
                    {describe(e)}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

// ════════════════ Ô số ════════════════

function KpiCard({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  accent?: "blue" | "amber" | "red";
}) {
  const color =
    accent === "blue"
      ? "text-blue-600"
      : accent === "amber"
        ? "text-amber-700"
        : accent === "red"
          ? "text-red-700"
          : "text-foreground";
  return (
    <div className="rounded-md border bg-surface p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className={cn("shrink-0", value > 0 ? color : "")}>{icon}</span>
        {/* Xuống dòng chứ KHÔNG cắt đuôi: ở khổ điện thoại 390px, hai trong
            năm nhãn bị cắt (đo 01/09/2026) và chỗ cắt rơi đúng vào chữ mang
            nghĩa — "Khiếu nại đang mở" thành "Khiếu nại đang …". */}
        <span className="min-w-0 leading-snug">{label}</span>
      </div>
      {/* Số 0 KHÔNG tô màu cảnh báo: "0 khiếu nại" là tin tốt, tô đỏ là báo
          động giả và làm loãng đúng lúc cần đỏ thật. */}
      <div className={cn("text-2xl font-semibold tabular-nums", value > 0 ? color : "")}>
        {value}
      </div>
    </div>
  );
}
